import React from "react";
import { X, Truck, Scales } from "@phosphor-icons/react";

/**
 * TruckPickerModal — Renders a list of truck visits in a given status and lets
 * the supervisor pick one. Used from the top-level TRUCK OUT and WEIGH OUT
 * action bar buttons so a supervisor doesn't have to scroll to the right panel.
 */
export default function TruckPickerModal({ title, subtitle, mode, visits, now, onClose, onPick }) {
    // mode: 'at_station' | 'pending_weigh_out'
    const isOut = mode === "at_station";
    const Icon = isOut ? Truck : Scales;
    const styles = isOut
        ? {
            headerBg: "bg-amber-500/20",
            headerBorder: "border-amber-500/50",
            iconText: "text-amber-300",
            rowBorder: "border-amber-500/40 hover:border-amber-400",
            elapsedText: "text-amber-300",
            actionText: "text-amber-300",
        }
        : {
            headerBg: "bg-sky-500/20",
            headerBorder: "border-sky-500/50",
            iconText: "text-sky-300",
            rowBorder: "border-sky-500/40 hover:border-sky-400",
            elapsedText: "text-sky-300",
            actionText: "text-sky-300",
        };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="truck-picker-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-full ${styles.headerBg} border ${styles.headerBorder} flex items-center justify-center`}>
                            <Icon size={18} weight="fill" className={styles.iconText} />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">{subtitle}</div>
                            <div className="text-lg font-black">{title}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-truck-picker">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-3 overflow-y-auto">
                    {visits.length === 0 ? (
                        <div className="text-center text-slate-500 py-8 text-sm">
                            {isOut ? "No trucks are currently loading at the station." : "No trucks are pending weigh-out."}
                        </div>
                    ) : (
                        <div className="space-y-1.5" data-testid="truck-picker-list">
                            {visits.map((v) => {
                                const ref = isOut ? v.arrived_at : (v.departed_at || v.arrived_at);
                                const elapsedMin = ref
                                    ? Math.max(0, Math.floor((now.getTime() - new Date(ref).getTime()) / 60000))
                                    : 0;
                                return (
                                    <button
                                        key={v.id}
                                        onClick={() => onPick(v)}
                                        className={`w-full rounded-lg border ${styles.rowBorder} bg-slate-800/60 hover:bg-slate-800 p-3 flex items-center justify-between gap-2 transition-colors`}
                                        data-testid={`truck-picker-row-${v.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon size={22} className={styles.iconText} weight={isOut ? "regular" : "fill"} />
                                            <div className="text-left">
                                                <div className="font-mono text-base font-black text-white leading-tight">{v.plate}</div>
                                                <div className="text-[10px] text-slate-400">
                                                    {v.skips} skip{v.skips === 1 ? "" : "s"} · Silo {v.silo_no} · <span className={styles.elapsedText}>{elapsedMin}m {isOut ? "loading" : "awaiting"}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`text-xs font-black uppercase tracking-wider ${styles.actionText}`}>
                                            {isOut ? "→ Weighbridge" : "Weigh Out"}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-slate-800">
                    <button onClick={onClose} className="w-full py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="truck-picker-cancel">Cancel</button>
                </div>
            </div>
        </div>
    );
}
